/**
 * Dashboard workspace helpers.
 *
 * These functions shape the shared dashboard payload into the usage cards,
 * highlights, and recent-activity summaries used by the Vue shell. The owner
 * dashboard remains commercial, while leader/member dashboards reuse the same
 * data through operational and read-only copy.
 *
 * @author Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
 */

function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function toLimit(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return null;
    }

    return parsed;
}

function parseDatasetDate(value) {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getDashboardRole(summary = {}) {
    /** Resolve the dashboard role used for layout and copy selection. */
    return summary.dashboardRole || 'none';
}

function countUploadsWithinDays(recentlyUploaded = [], days = 7) {
    const parsedDates = recentlyUploaded
        .map((item) => parseDatasetDate(item?.datasetDate))
        .filter(Boolean);
    if (!parsedDates.length) {
        return 0;
    }

    const newest = parsedDates.reduce(
        (latest, candidate) => (candidate > latest ? candidate : latest),
        parsedDates[0]
    );
    const threshold = newest.getTime() - days * 24 * 60 * 60 * 1000;

    return parsedDates.filter((date) => date.getTime() >= threshold).length;
}

export function formatCount(value) {
    return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
    }).format(toNumber(value));
}

export function formatStorageFromBytes(bytes = 0) {
    const safeBytes = Math.max(toNumber(bytes), 0);
    const gb = safeBytes / 1024 ** 3;

    if (gb >= 100) {
        return `${Math.round(gb)} GB`;
    }

    if (gb >= 10) {
        return `${gb.toFixed(1)} GB`;
    }

    return `${gb.toFixed(2)} GB`;
}

export function formatStorageAllowance(limitGb) {
    const limit = toLimit(limitGb);
    if (limit === null) return 'Custom';
    if (limit === -1) return 'Unlimited';
    return `${formatCount(limit)} GB`;
}

export function formatLimitLabel(limit, noun = 'items') {
    const normalized = toLimit(limit);
    if (normalized === null) return `Custom ${noun}`;
    if (normalized === -1) return `Unlimited ${noun}`;
    return `${formatCount(normalized)} ${noun}`;
}

export function usageProgress(value, limit) {
    const normalizedLimit = toLimit(limit);
    if (normalizedLimit === null || normalizedLimit <= 0) {
        return null;
    }

    return Math.min(
        100,
        Math.max(0, Math.round((toNumber(value) / normalizedLimit) * 100))
    );
}

export function buildDatasetCards(recentlyUploaded = [], recentViews = []) {
    /** Build the owner-facing dataset cards used in the commercial dashboard. */
    const byId = new Map();
    const allViews = [...recentlyUploaded, ...recentViews];

    allViews.forEach((view, index) => {
        if (!view || !view.viewUuid || byId.has(view.viewUuid)) {
            return;
        }

        byId.set(view.viewUuid, {
            id: view.viewUuid,
            name: view.assetName || 'Untitled dataset',
            workspace: view.siteName || 'Workspace',
            date: view.datasetDate || '',
            status: view.status || (index < recentlyUploaded.length ? 'Published' : 'Viewed'),
            statusTone: (() => {
                const normalized = String(
                    view.status || (index < recentlyUploaded.length ? 'Published' : 'Viewed')
                )
                    .toLowerCase()
                    .replace(/[^a-z]/g, '');

                if (normalized.includes('processing') || normalized.includes('queued')) {
                    return 'warning';
                }

                if (normalized.includes('draft') || normalized.includes('viewed')) {
                    return 'info';
                }

                return 'success';
            })(),
            viewType: view.viewType || 'ortho_view',
            viewerUrl: view.link || '/view',
            manageUrl: view.link || '/view',
            image: view.image || '',
        });
    });

    return Array.from(byId.values()).slice(0, 6);
}

export function buildUploadTrend(recentlyUploaded = []) {
    /** Summarize recent uploads into a small trend series for charts. */
    const grouped = new Map();

    recentlyUploaded.forEach((item) => {
        const key = item?.datasetDate || 'Recent';
        grouped.set(key, (grouped.get(key) || 0) + 1);
    });

    const entries = Array.from(grouped.entries()).slice(-6);
    return {
        categories: entries.map(([label]) => label),
        values: entries.map(([, value]) => value),
    };
}

export function buildNotificationItems(
    summary = {},
    recentlyUploaded = [],
    teams = []
) {
    /** Build dashboard notification cards from usage, plan, and team data. */
    const dashboardRole = getDashboardRole(summary);
    const usage = summary.usage || {};
    const plan = summary.plan || {};
    const notices = [];
    const storageProgress = usageProgress(
        toNumber(usage.storageUsedBytes) / 1024 ** 3,
        usage.storageAllowedGb
    );
    const datasetCount = toNumber(usage.datasets ?? usage.datasetsUsed);
    const datasetProgress = usageProgress(
        datasetCount,
        usage.datasetsAllowed
    );

    if (dashboardRole === 'owner' && storageProgress !== null && storageProgress >= 80) {
        notices.push({
            tone: 'warning',
            title: 'Storage is nearing its limit',
            meta: 'Today',
            body: `${formatStorageFromBytes(
                usage.storageUsedBytes
            )} of ${formatStorageAllowance(usage.storageAllowedGb)} is already allocated.`,
        });
    }

    if (dashboardRole === 'owner' && datasetProgress !== null && datasetProgress >= 80) {
        notices.push({
            tone: 'info',
            title: 'Dataset quota is nearly full',
            meta: 'Today',
            body: `${formatCount(datasetCount)} of ${formatLimitLabel(
                usage.datasetsAllowed,
                'datasets'
            )} is currently in use.`,
        });
    }

    if (dashboardRole === 'owner' && plan.renewalDate) {
        notices.push({
            tone: 'neutral',
            title: 'Subscription visibility',
            meta: 'Renewal watch',
            body: `${plan.name || 'Workspace plan'} renews on ${plan.renewalDate}.`,
        });
    }

    if (recentlyUploaded.length) {
        notices.push({
            tone: 'success',
            title: 'Upload completed',
            meta: 'Recent',
            body: `${recentlyUploaded[0].assetName || 'A dataset'} was recently published and is ready to review.`,
        });
    }

    if (teams.length) {
        notices.push({
            tone: 'info',
            title: 'Access teams',
            meta: 'Workspace',
            body: `${formatCount(teams.length)} access team${teams.length === 1 ? '' : 's'} are ready for collaboration and access review.`,
        });
    }

    if (dashboardRole === 'owner' && plan.renewalDate) {
        notices.push({
            tone: 'warning',
            title: 'Billing reminders',
            meta: plan.renewalDate,
            body: `Keep an eye on renewal and payment details before ${plan.renewalDate}.`,
        });
    }

    if (!notices.length) {
        notices.push({
            tone: 'success',
            title: 'Workspace is healthy',
            meta: 'Live',
            body:
                dashboardRole === 'team_member'
                    ? 'Viewer access, dataset visibility, and recent activity look healthy.'
                    : dashboardRole === 'team_leader'
                      ? 'Upload access and operational dataset management are ready for your next task.'
                      : 'Uploads, quota visibility, and billing access look ready for your next dataset.',
        });
    }

    return notices.slice(0, 6);
}

export function buildAnalyticsHighlights(
    summary = {},
    recentlyUploaded = [],
    teams = []
) {
    /** Build the metric groups shown in the dashboard analytics panel. */
    const dashboardRole = getDashboardRole(summary);
    const usage = summary.usage || {};
    const datasetCount = toNumber(usage.datasets ?? usage.datasetsUsed);
    const storageAllowedBytes = toNumber(usage.storageAllowedBytes);
    const storageUsedBytes = toNumber(usage.storageUsedBytes);
    const storageRemainingBytes =
        storageAllowedBytes > 0
            ? Math.max(storageAllowedBytes - storageUsedBytes, 0)
            : null;
    const uploadsLastWeek = countUploadsWithinDays(recentlyUploaded, 7);
    const uploadsLastMonth = countUploadsWithinDays(recentlyUploaded, 30);
    const publishedUploads = recentlyUploaded.filter(Boolean).length;
    const uploadWindow = countUploadsWithinDays(recentlyUploaded, 7);

    return {
        storage: [
            {
                label: dashboardRole === 'owner' ? 'Weekly growth' : 'Weekly activity',
                value: `${uploadsLastWeek} uploads`,
            },
            {
                label: 'Monthly growth',
                value: `${uploadsLastMonth} uploads`,
            },
            {
                label: 'Remaining capacity',
                value:
                    dashboardRole !== 'owner'
                        ? 'Owner only'
                        : storageRemainingBytes === null
                        ? 'Custom limit'
                        : formatStorageFromBytes(storageRemainingBytes),
            },
        ],
        dataset: [
            {
                label:
                    dashboardRole === 'owner'
                        ? 'Dataset Groups'
                        : dashboardRole === 'team_leader'
                          ? 'Datasets I Manage'
                          : 'Datasets I Can Access',
                value: formatCount(
                    dashboardRole === 'owner' ? usage.datasetGroups : datasetCount
                ),
            },
            {
                label: 'Datasets',
                value: formatCount(usage.datasets ?? usage.datasetsUsed),
            },
            {
                label: 'Assets',
                value: formatCount(usage.assets),
            },
            {
                label: 'Recent uploads',
                value: formatCount(publishedUploads),
            },
        ],
        workspace: [
            {
                label:
                    dashboardRole === 'owner'
                        ? 'Billing Teams'
                        : dashboardRole === 'team_leader'
                          ? 'Teams You Lead'
                          : 'Teams You Belong To',
                value: formatCount(
                    dashboardRole === 'owner'
                        ? usage.billingTeams ?? usage.seatsUsed
                        : usage.myTeams || teams.length
                ),
            },
            {
                label: 'Access Teams',
                value: formatCount(usage.accessTeams),
            },
            {
                label: 'My Teams',
                value: formatCount(usage.myTeams || teams.length),
            },
            {
                label: 'Upload activity',
                value: formatCount(uploadWindow || publishedUploads),
            },
        ],
    };
}

export function buildActivityItems(recentlyUploaded = [], teams = []) {
    /** Build the compact activity feed items for the dashboard sidebar. */
    const uploadItems = recentlyUploaded.slice(0, 3).map((item) => ({
        tone: 'success',
        title: `${item.assetName || 'Dataset'} is ready`,
        meta: item.datasetDate || 'Recently updated',
    }));

    const teamItems = teams.slice(0, 2).map((team) => ({
        tone: 'info',
        title: `${team.name} access team is available`,
        meta: `${formatCount(team.memberCount)} members · ${team.role}`,
    }));

    return [...uploadItems, ...teamItems].slice(0, 5);
}

export function buildUsageCards(summary = {}) {
    /** Build the large summary cards shown near the top of the dashboard. */
    const dashboardRole = getDashboardRole(summary);
    const usage = summary.usage || {};
    const datasetsAllowed = toLimit(usage.datasetsAllowed);
    const datasetGroupCount = toNumber(usage.datasetGroups);
    const datasetCount = toNumber(usage.datasets ?? usage.datasetsUsed);
    const billingTeamCount = toNumber(usage.billingTeams ?? usage.seatsUsed);
    const accessTeamCount = toNumber(usage.accessTeams);

    if (dashboardRole === 'team_leader') {
        return [
            {
                id: 'teams-led',
                label: 'Teams You Lead',
                value: formatCount(usage.myTeams || accessTeamCount),
                detail: 'Operational teams you lead',
                remaining: null,
                progress: null,
                icon: '$vuetify.icons.teams',
                tone: 'info',
                help: 'Teams where you can manage dataset access and uploads.',
            },
            {
                id: 'datasets-managed',
                label: 'Datasets I Manage',
                value: formatCount(datasetCount),
                detail: 'Datasets attached to your teams',
                remaining: `${formatCount(datasetGroupCount)} assets`,
                progress: null,
                icon: 'mdi-database',
                tone: 'success',
                help: 'Datasets you can manage through teams you lead.',
            },
            {
                id: 'assets-access',
                label: 'Assets I Can Access',
                value: formatCount(usage.assets),
                detail: 'Accessible through led teams',
                remaining: null,
                progress: null,
                icon: 'mdi-cube-outline',
                tone: 'warning',
                help: 'Assets reachable through team access.',
            },
        ];
    }

    if (dashboardRole === 'team_member') {
        return [
            {
                id: 'teams-member',
                label: 'Teams You Belong To',
                value: formatCount(usage.myTeams || accessTeamCount),
                detail: 'Read-only team access',
                remaining: null,
                progress: null,
                icon: '$vuetify.icons.teams',
                tone: 'info',
                help: 'Teams that grant your current workspace access.',
            },
            {
                id: 'datasets-access',
                label: 'Datasets I Can Access',
                value: formatCount(datasetCount),
                detail: 'Available through your teams',
                remaining: `${formatCount(datasetGroupCount)} assets`,
                progress: null,
                icon: 'mdi-database',
                tone: 'success',
                help: 'Datasets you can browse and open in the viewer.',
            },
            {
                id: 'assets-access',
                label: 'Assets I Can Access',
                value: formatCount(usage.assets),
                detail: 'Available for browsing',
                remaining: null,
                progress: null,
                icon: 'mdi-cube-outline',
                tone: 'warning',
                help: 'Assets shared with teams you belong to.',
            },
        ];
    }

    return [
        {
            id: 'datasets',
            label: 'Datasets',
            value: formatCount(datasetCount),
            detail: 'Published datasets inside groups',
            remaining:
                datasetsAllowed === -1
                    ? 'Unlimited'
                    : usage.datasetsAllowed === null
                      ? 'Custom allowance'
                      : `${formatCount(Math.max(datasetsAllowed - datasetCount, 0))} remaining`,
            progress: usageProgress(datasetCount, usage.datasetsAllowed),
            icon: 'mdi-database',
            tone: 'success',
            help: 'Published datasets contained within Dataset Groups.',
        },
        {
            id: 'access-teams',
            label: 'Access Teams',
            value: formatCount(accessTeamCount),
            detail: 'Teams granted access',
            secondary: `${formatCount(billingTeamCount)} Billing Teams - Measured against plan limits`,
            remaining: null,
            progress: null,
            icon: '$vuetify.icons.teams',
            tone: 'info',
            help: 'Teams granted access to datasets owned by this Billing Account.',
        },
        {
            id: 'storage',
            label: 'Storage',
            value: formatStorageFromBytes(usage.storageUsedBytes),
            detail: formatStorageAllowance(usage.storageAllowedGb),
            remaining:
                usage.storageAllowedBytes === null
                    ? 'Custom storage'
                    : formatStorageFromBytes(
                          Math.max(
                              toNumber(usage.storageAllowedBytes) -
                                  toNumber(usage.storageUsedBytes),
                              0
                          )
                      ),
            progress: usageProgress(
                toNumber(usage.storageUsedBytes) / 1024 ** 3,
                usage.storageAllowedGb
            ),
            icon: 'mdi-harddisk',
            tone: 'warning',
            help: 'Storage consumed by Dataset Groups owned by this Billing Account.',
        },
        {
            id: 'assets',
            label: 'Assets',
            value: formatCount(usage.assets),
            detail: 'Asset containers owned by this billing account',
            remaining: toNumber(usage.assets)
                ? `${formatCount(datasetGroupCount)} dataset groups`
                : 'Assets will calculate as you create groups',
            progress: null,
            icon: 'mdi-cube-outline',
            tone: 'warning',
            help: 'Asset containers owned by this Billing Account.',
        },
    ];
}
